import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, BRL, LOGO_URL } from "@/lib/api";
import { STATUS_FLOW, STATUS_META, nextStatus, nextStatusLabel } from "@/lib/status";
import Receipt from "@/components/Receipt";
import ProductManager from "@/components/ProductManager";
import NeighborhoodManager from "@/components/NeighborhoodManager";
import CardFeeSettings from "@/components/CardFeeSettings";
import StoreHoursSettings from "@/components/StoreHoursSettings";
import SlideManager from "@/components/SlideManager";
import NoticeSettings from "@/components/NoticeSettings";
import StaffPushCard from "@/components/StaffPushCard";
import { Printer, MessageCircle, LogOut, RefreshCw, X, Clock, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const COLUMNS = STATUS_FLOW.filter((s) => s !== "entregue");

export default function Dashboard() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [printOrder, setPrintOrder] = useState(null);
  const [view, setView] = useState("pedidos");
  const [storeWa, setStoreWa] = useState("");

  useEffect(() => {
    if (ready && !user) navigate("/gestao/login");
  }, [ready, user, navigate]);

  useEffect(() => {
    api.get("/store").then(({ data }) => setStoreWa((data.whatsapp || "").replace(/\D/g, ""))).catch(() => {});
  }, []);

  const load = useCallback(() => {
    api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      load();
      const t = setInterval(load, 7000);
      return () => clearInterval(t);
    }
  }, [user, load]);

  const advance = async (order) => {
    const ns = nextStatus(order.status);
    if (!ns) return;
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}/status`, { status: ns });
      setOrders((prev) => prev.map((o) => (o.id === data.id ? data : o)));
      toast.success(`Pedido #${order.order_number}: ${STATUS_META[ns].label}`);
    } catch {
      toast.error("Erro ao atualizar status.");
    }
  };

  const cancel = async (order) => {
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}/status`, { status: "cancelado" });
      setOrders((prev) => prev.map((o) => (o.id === data.id ? data : o)));
      toast("Pedido cancelado");
    } catch {
      toast.error("Erro ao cancelar.");
    }
  };

  const resetCounter = async () => {
    if (!window.confirm("Resetar a numeração dos pedidos? O próximo pedido será o #1.")) return;
    try {
      await api.post("/admin/orders/reset-counter");
      toast.success("Numeração resetada. O próximo pedido será o #1.");
    } catch {
      toast.error("Erro ao resetar a numeração.");
    }
  };

  const doPrint = (order) => {
    setPrintOrder(order);
    setTimeout(() => window.print(), 120);
  };

  const sendWhatsApp = (order) => {
    const lines = order.items.map((i) => `• ${i.qty}x ${i.name} - ${BRL(i.price * i.qty)}`).join("\n");
    const isPickup = order.fulfillment_type === "retirada";
    const feeLine =
      order.delivery_fee != null
        ? `Subtotal: ${BRL(order.subtotal ?? order.total)}\n${isPickup ? "Retirada: Grátis" : `Taxa de entrega: ${BRL(order.delivery_fee)}`}\n${order.card_fee > 0 ? `Taxa do cartão${order.card_fee_percent ? ` (${order.card_fee_percent}%)` : ""}: ${BRL(order.card_fee)}\n` : ""}`
        : "";
    const destino = isPickup
      ? "Retirada no balcão"
      : `Endereço: ${order.address}${order.neighborhood ? ` (${order.neighborhood})` : ""}`;
    const msg =
      `*Paulista's Lanches & Pizzas* - Pedido #${order.order_number}\n` +
      `Cliente: ${order.customer_name}\n\n${lines}\n\n` +
      feeLine +
      `*Total: ${BRL(order.total)}*\nPagamento: ${order.payment_method}\n` +
      `${destino}\nStatus: ${STATUS_META[order.status].label}`;
    let phone = (order.phone || "").replace(/\D/g, "").replace(/^0+/, "");
    if (phone.startsWith("55") && phone.length >= 12) phone = phone.slice(2);
    const wa = phone.length >= 10 ? `55${phone}` : storeWa;
    window.open(`https://wa.me/${wa}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  if (!ready || !user)
    return <div className="min-h-screen grid place-items-center bg-[#0C0A09] text-stone-400">Carregando...</div>;

  const activeOrders = orders.filter((o) => o.status !== "entregue" && o.status !== "cancelado");
  const finished = orders.filter((o) => o.status === "entregue" || o.status === "cancelado");

  return (
    <div className="min-h-screen bg-[#0C0A09] text-stone-100">
      <header className="no-print sticky top-0 z-40 bg-[#1C1917] border-b border-[#292524]">
        <div className="max-w-[1600px] mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Paulista's Lanches & Pizzas" className="h-10 w-auto object-contain" />
            <div>
              <p className="font-display font-black text-lg tracking-tight leading-none">Gestão de Pedidos</p>
              <p className="text-xs text-stone-400">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center bg-[#0C0A09] border border-[#292524] rounded-full p-1 mr-1">
              <button onClick={() => setView("pedidos")} data-testid="view-pedidos" className={`px-3 py-1.5 text-sm rounded-full transition-colors ${view === "pedidos" ? "bg-primary text-primary-foreground" : "text-stone-300 hover:text-white"}`}>Pedidos</button>
              <button onClick={() => setView("cardapio")} data-testid="view-cardapio" className={`px-3 py-1.5 text-sm rounded-full transition-colors ${view === "cardapio" ? "bg-primary text-primary-foreground" : "text-stone-300 hover:text-white"}`}>Cardápio</button>
              <button onClick={() => setView("taxas")} data-testid="view-taxas" className={`px-3 py-1.5 text-sm rounded-full transition-colors ${view === "taxas" ? "bg-primary text-primary-foreground" : "text-stone-300 hover:text-white"}`}>Taxas</button>
              <button onClick={() => setView("carrossel")} data-testid="view-carrossel" className={`px-3 py-1.5 text-sm rounded-full transition-colors ${view === "carrossel" ? "bg-primary text-primary-foreground" : "text-stone-300 hover:text-white"}`}>Carrossel</button>
            </div>
            {view === "pedidos" && (
              <button onClick={resetCounter} data-testid="reset-counter-btn" className="inline-flex items-center gap-1.5 rounded-full border border-[#292524] px-3 py-2 text-sm hover:bg-[#292524] transition-colors">
                <RotateCcw className="w-4 h-4" /> <span className="hidden md:inline">Resetar nº</span>
              </button>
            )}
            {view === "pedidos" && (
              <button onClick={load} data-testid="refresh-btn" className="inline-flex items-center gap-1.5 rounded-full border border-[#292524] px-3 py-2 text-sm hover:bg-[#292524] transition-colors">
                <RefreshCw className="w-4 h-4" /> Atualizar
              </button>
            )}
            <button onClick={logout} data-testid="logout-btn" className="inline-flex items-center gap-1.5 rounded-full border border-[#292524] px-3 py-2 text-sm hover:bg-[#292524] transition-colors">
              <LogOut className="w-4 h-4" /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="no-print max-w-[1600px] mx-auto px-5 py-6">
        <div className="sm:hidden flex items-center bg-[#0C0A09] border border-[#292524] rounded-full p-1 mb-4">
          <button onClick={() => setView("pedidos")} className={`flex-1 px-3 py-1.5 text-sm rounded-full transition-colors ${view === "pedidos" ? "bg-primary text-primary-foreground" : "text-stone-300"}`}>Pedidos</button>
          <button onClick={() => setView("cardapio")} className={`flex-1 px-3 py-1.5 text-sm rounded-full transition-colors ${view === "cardapio" ? "bg-primary text-primary-foreground" : "text-stone-300"}`}>Cardápio</button>
          <button onClick={() => setView("taxas")} className={`flex-1 px-3 py-1.5 text-sm rounded-full transition-colors ${view === "taxas" ? "bg-primary text-primary-foreground" : "text-stone-300"}`}>Taxas</button>
          <button onClick={() => setView("carrossel")} data-testid="view-carrossel-mobile" className={`flex-1 px-3 py-1.5 text-sm rounded-full transition-colors ${view === "carrossel" ? "bg-primary text-primary-foreground" : "text-stone-300"}`}>Carrossel</button>
        </div>

        {view === "cardapio" && <ProductManager />}
        {view === "carrossel" && <SlideManager />}
        {view === "taxas" && (
          <>
            <StoreHoursSettings />
            <NoticeSettings />
            <CardFeeSettings />
            <NeighborhoodManager />
          </>
        )}

        {view === "pedidos" && (
        <>
          <StaffPushCard />
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
          {COLUMNS.map((col) => {
            const colOrders = activeOrders.filter((o) => o.status === col);
            const meta = STATUS_META[col];
            return (
              <div key={col} data-testid={`kanban-col-${col}`} className="bg-[#1C1917] rounded-2xl border border-[#292524] p-3 flex flex-col min-h-[300px]">
                <div className="flex items-center justify-between px-1 mb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: meta.color }} />
                    <span className="font-display font-bold">{meta.label}</span>
                  </div>
                  <span className="text-xs bg-[#292524] rounded-full px-2 py-0.5">{colOrders.length}</span>
                </div>
                <div className="space-y-3 flex-1">
                  {colOrders.length === 0 && (
                    <p className="text-xs text-stone-500 text-center py-8">Nenhum pedido</p>
                  )}
                  {colOrders.map((o) => (
                    <OrderCard
                      key={o.id}
                      order={o}
                      onAdvance={() => advance(o)}
                      onCancel={() => cancel(o)}
                      onPrint={() => doPrint(o)}
                      onWhatsApp={() => sendWhatsApp(o)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {finished.length > 0 && (
          <div className="mt-8">
            <h2 className="font-display font-bold text-lg mb-3 text-stone-300">Finalizados</h2>
            <div className="grid gap-3 grid-cols-1 md:grid-cols-3 xl:grid-cols-4">
              {finished.slice(0, 12).map((o) => (
                <div key={o.id} className="bg-[#1C1917] border border-[#292524] rounded-xl p-3 opacity-80">
                  <div className="flex justify-between items-center">
                    <span className="font-bold">#{o.order_number}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: STATUS_META[o.status].color, color: "#fff" }}>
                      {STATUS_META[o.status].label}
                    </span>
                  </div>
                  <p className="text-sm text-stone-400 mt-1">{o.customer_name} · {BRL(o.total)}</p>
                  <button onClick={() => doPrint(o)} className="mt-2 text-xs inline-flex items-center gap-1 text-stone-400 hover:text-stone-100 transition-colors">
                    <Printer className="w-3.5 h-3.5" /> Imprimir
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        </>
        )}
      </main>

      <Receipt order={printOrder} />
    </div>
  );
}

function OrderCard({ order, onAdvance, onCancel, onPrint, onWhatsApp }) {
  const ns = nextStatus(order.status);
  const time = new Date(order.created_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  return (
    <div data-testid={`order-card-${order.order_number}`} className="bg-[#0C0A09] border border-[#292524] rounded-xl p-3">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-display font-black">#{order.order_number}</p>
          <p className="text-sm text-stone-300">{order.customer_name}</p>
        </div>
        <span className="text-xs text-stone-500 flex items-center gap-1"><Clock className="w-3 h-3" />{time}</span>
      </div>
      <div className="mt-2 space-y-0.5">
        {order.items.map((it, i) => (
          <p key={i} className="text-xs text-stone-400">{it.qty}x {it.name}</p>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${order.fulfillment_type === "retirada" ? "bg-teal-500/20 text-teal-300" : "bg-blue-500/20 text-blue-300"}`}>
          {order.fulfillment_type === "retirada" ? "Retirada" : "Entrega"}
        </span>
        {order.fulfillment_type !== "retirada" && order.neighborhood && (
          <span className="text-[10px] text-stone-400">{order.neighborhood} · taxa {BRL(order.delivery_fee || 0)}</span>
        )}
      </div>
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#292524]">
        <span className="font-bold text-secondary">{BRL(order.total)}</span>
        <span className="text-xs text-stone-500">{order.payment_method}</span>
      </div>
      <p className="text-xs text-stone-500 mt-1 truncate">
        {order.fulfillment_type === "retirada" ? "Retirada no balcão" : order.address}
      </p>

      {ns && (
        <button
          onClick={onAdvance}
          data-testid={`advance-btn-${order.order_number}`}
          className="w-full mt-3 rounded-full bg-primary text-primary-foreground py-2 text-sm font-semibold hover:bg-primary/90 transition-colors"
        >
          {nextStatusLabel[order.status]}
        </button>
      )}
      <div className="flex gap-2 mt-2">
        <button onClick={onPrint} data-testid={`print-btn-${order.order_number}`} className="flex-1 inline-flex items-center justify-center gap-1 rounded-full border border-[#292524] py-1.5 text-xs hover:bg-[#292524] transition-colors">
          <Printer className="w-3.5 h-3.5" /> Imprimir
        </button>
        <button onClick={onWhatsApp} data-testid={`whatsapp-btn-${order.order_number}`} className="flex-1 inline-flex items-center justify-center gap-1 rounded-full border border-[#292524] py-1.5 text-xs hover:bg-[#292524] transition-colors">
          <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
        </button>
        <button onClick={onCancel} data-testid={`cancel-btn-${order.order_number}`} className="inline-flex items-center justify-center rounded-full border border-[#292524] w-8 py-1.5 text-xs text-destructive hover:bg-destructive/10 transition-colors">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
